import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { getWindowSafe } from '@change-corgi/core/window';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import type { ActionsReturnType, GroupSharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';
const SHARING_EVENT_PREFIX = 'ready_to_share_reddit_group_post';
// eslint-disable-next-line max-lines-per-function
export function useRedditGroupPostSharing(props: GroupSharingHookProps): ActionsReturnType {
	const track = useTracking();
	const i18n = useI18n();
	const window = getWindowSafe();
	const { resolveUrl } = useUtilityContext();
	const currentUserId = useCurrentUserId();

	const {
		recommendedGroup,
		message,
		petitionTitle = '',
		shareUrl = '',
		trackingProps: { context, viewContext },
	} = props;

	const shareUrlWithUtmParams = useMemo(() => {
		if (!shareUrl) return '';

		let slug: string | undefined;

		try {
			const url = new URL(shareUrl);
			slug = url.pathname.match(/\/p\/([^/?]+)/)?.[1];
		} catch {
			const match = shareUrl.match(/\/p\/([^/?]+)/);
			slug = match?.[1];
		}

		if (!slug) return shareUrl;

		const trackParams = qs.stringify(
			{
				recruiter: currentUserId || undefined,
				utm_source: 'share_petition',
				utm_campaign: 'starter_dashboard',
				utm_medium: 'reddit_post',
				utm_term: 'starter_dashboard_web',
			},
			{ encodeValuesOnly: true },
		);

		return resolveUrl(`/p/${slug}?${trackParams}`);
	}, [shareUrl, resolveUrl, currentUserId]);

	const primaryAction = useCallback(() => {
		void copyToClipboard(message, i18n);

		const trackingData = {
			context,
			location: 'reddit_group',
			tab_at_share: viewContext,
			app_available: false,
			content: 'post',
		};
		void track(TRACKING_EVENT_NAME, trackingData);

		if (window) {
			const redditUrl = `https://www.reddit.com/${recommendedGroup?.name}/submit?title=${encodeURIComponent(petitionTitle)}&url=${encodeURIComponent(shareUrlWithUtmParams || shareUrl)}`;
			window.open(redditUrl, '_blank');
			void track(`${SHARING_EVENT_PREFIX}_success`, trackingData);
		} else {
			void track(`${SHARING_EVENT_PREFIX}_failure`, trackingData);
		}
	}, [
		context,
		i18n,
		message,
		petitionTitle,
		recommendedGroup?.name,
		shareUrl,
		shareUrlWithUtmParams,
		track,
		viewContext,
		window,
	]);

	const secondaryAction = useCallback(() => {
		const trackingData = {
			context,
			location: 'reddit_group',
			tab_at_share: viewContext,
			app_available: false,
			content: 'view_group',
		};

		void track(TRACKING_EVENT_NAME, trackingData);
		if (window) {
			window.open(recommendedGroup?.url, '_blank');
		}
	}, [context, recommendedGroup?.url, track, viewContext, window]);

	return {
		primaryAction,
		secondaryAction,
	};
}
